<template>
  <div class="tcp-wrap">

    <!-- ── ISSUE IDENTITY PILL ───────────────────────── -->
    <!-- <div class="tcp-issue-pill" v-if="refNum">
      <span class="tcp-pill-ref">{{ refNum }}</span>
      <span class="tcp-pill-name">{{ issueName }}</span>
    </div> -->

    <!-- ── SVG CLOCK ──────────────────────────────────── -->
    <div class="tcp-clock-card">
      <svg class="tcp-svg" viewBox="0 0 320 320" aria-hidden="true">
        <defs>
          <linearGradient id="tcp-g-arc" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stop-color="#6366f1"/>
            <stop offset="55%"  stop-color="#8b5cf6"/>
            <stop offset="100%" stop-color="#06b6d4"/>
          </linearGradient>
          <linearGradient id="tcp-g-over" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stop-color="#f43f5e"/>
            <stop offset="100%" stop-color="#f97316"/>
          </linearGradient>
          <filter id="tcp-f-shadow" x="-15%" y="-15%" width="130%" height="130%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="#6366f1" flood-opacity="0.18"/>
          </filter>
          <filter id="tcp-f-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="tcp-f-dot" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        <circle cx="160" cy="160" r="150" fill="none" stroke="#f1f5f9" stroke-width="1"/>

        <g v-for="i in 60" :key="'tick'+i">
          <line
            :x1="160 + 140 * cos(i * 6 - 90)" :y1="160 + 140 * sin(i * 6 - 90)"
            :x2="160 + (i % 5 === 0 ? 128 : 134) * cos(i * 6 - 90)"
            :y2="160 + (i % 5 === 0 ? 128 : 134) * sin(i * 6 - 90)"
            :stroke="i % 5 === 0 ? '#c7d2fe' : '#e2e8f0'"
            :stroke-width="i % 5 === 0 ? 2.5 : 1" stroke-linecap="round"
          />
        </g>

        <circle cx="160" cy="160" r="118" fill="none" stroke="#e8eaf6" stroke-width="10" filter="url(#tcp-f-shadow)"/>

        <circle cx="160" cy="160" r="118" fill="none"
          :stroke="overtime ? 'url(#tcp-g-over)' : 'url(#tcp-g-arc)'"
          stroke-width="20" stroke-linecap="round"
          :stroke-dasharray="ARC_CIRC" :stroke-dashoffset="arcOffset"
          opacity="0.35" filter="url(#tcp-f-glow)"
        />

        <circle class="tcp-arc" cx="160" cy="160" r="118"
          :stroke="overtime ? 'url(#tcp-g-over)' : 'url(#tcp-g-arc)'"
          :stroke-dasharray="ARC_CIRC" :stroke-dashoffset="arcOffset"
        />

        <g v-for="h in 12" :key="'h'+h">
          <circle :cx="160 + 148 * cos(h * 30 - 90)" :cy="160 + 148 * sin(h * 30 - 90)"
            r="2.5" :fill="h % 3 === 0 ? '#c7d2fe' : '#dde1f7'"/>
        </g>

        <!-- <circle class="tcp-dot" r="7"
          :cx="160 + 118 * cos(currentSeconds * 6 - 90)"
          :cy="160 + 118 * sin(currentSeconds * 6 - 90)"
          filter="url(#tcp-f-dot)"
        />
        <circle r="3" fill="white" opacity="0.7"
          :cx="160 + 118 * cos(currentSeconds * 6 - 90)"
          :cy="160 + 118 * sin(currentSeconds * 6 - 90)"
        /> -->
      </svg>

      <div class="tcp-digits-wrap">
        <div class="tcp-time" :class="{ 'tcp-time--pulse': pulse }">{{ displayTime }}</div>
        <div class="tcp-sub">
          <span class="tcp-elapsed">elapsed</span>
          <template v-if="estimate">
            <span class="tcp-sep">·</span>
            <span class="tcp-est">est {{ formatEstimate(estimate) }}</span>
          </template>
        </div>
      </div>
    </div>

    <!-- ── H / M / S ──────────────────────────────────── -->
    <v-row no-gutters class="tcp-hms-row">
      <v-col v-for="(u, i) in hmsUnits" :key="i" class="tcp-hms-cell">
        <div class="tcp-hms-val">{{ u.val }}</div>
        <div class="tcp-hms-lbl">{{ u.lbl }}</div>
      </v-col>
    </v-row>

    <!-- ── ESTIMATE PROGRESS ──────────────────────────── -->
    <div class="tcp-progress-card" v-if="estimate">
      <div class="tcp-progress-header">
        <span class="tcp-progress-title">
          <v-icon size="13" color="#6366f1">mdi-chart-timeline-variant</v-icon>
          Session Progress
        </span>
        <span class="tcp-progress-pct" :class="{ 'tcp-pct--over': progressPct >= 100 }">
          {{ progressPct }}%
        </span>
      </div>
      <div class="tcp-progress-track">
        <div class="tcp-progress-fill"
          :class="{ 'tcp-fill--over': progressPct >= 100 }"
          :style="{ width: Math.min(progressPct, 100) + '%' }"
        ></div>
      </div>
      <div class="tcp-progress-ticks">
        <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
      </div>
    </div>

    <!-- ── STOP BUTTON ────────────────────────────────── -->
    <button class="tcp-stop-btn" @click="$emit('stop')" :disabled="stopping">
      <span class="tcp-stop-ring"></span>
      <span class="tcp-stop-content">
        <span class="tcp-stop-icon"><span class="tcp-stop-sq"></span></span>
        <span class="tcp-stop-label">{{ stopping ? 'Stopping…' : 'Stop Session' }}</span>
      </span>
    </button>

    <!-- ── KBD HINT ───────────────────────────────────── -->
    <!-- <div class="tcp-kbd-hint"><kbd>Esc</kbd> go back</div> -->

  </div>
</template>

<script>
const RAD      = Math.PI / 180;
const ARC_R    = 118;
const ARC_CIRC = 2 * Math.PI * ARC_R;

export default {
  name: 'TimerClockPanel',

  props: {
    elapsedSeconds: { type: Number,  default: 0 },
    estimate:       { type: Number,  default: null },
    pulse:          { type: Boolean, default: false },
    stopping:       { type: Boolean, default: false },
    overtime:       { type: Boolean, default: false },
    refNum:         { type: String,  default: '' },
    issueName:      { type: String,  default: '' },
  },

  emits: ['stop'],

  data() {
    return { ARC_CIRC };
  },

  computed: {
    displayTime() {
      const s = this.elapsedSeconds;
      return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
        .map(v => String(v).padStart(2, '0')).join(':');
    },
    hoursDisplay()  { return String(Math.floor(this.elapsedSeconds / 3600)).padStart(2, '0'); },
    minutesDisplay(){ return String(Math.floor((this.elapsedSeconds % 3600) / 60)).padStart(2, '0'); },
    secondsDisplay(){ return String(this.elapsedSeconds % 60).padStart(2, '0'); },
    currentSeconds(){ return this.elapsedSeconds % 60; },
    hmsUnits() {
      return [
        { val: this.hoursDisplay,   lbl: 'HRS' },
        { val: this.minutesDisplay, lbl: 'MIN' },
        { val: this.secondsDisplay, lbl: 'SEC' },
      ];
    },
    arcOffset() { return ARC_CIRC - (this.currentSeconds / 60) * ARC_CIRC; },
    progressPct() {
      if (!this.estimate) return 0;
      return Math.min(Math.round((this.elapsedSeconds / (this.estimate * 60)) * 100), 100);
    },
  },

  methods: {
    cos(deg) { return Math.cos(deg * RAD); },
    sin(deg) { return Math.sin(deg * RAD); },
    formatEstimate(m) {
      if (!m) return '—';
      return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
    },
  },
};
</script>

<style scoped>
.tcp-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 24px 32px 28px;
  height: 100%;
  background: linear-gradient(170deg, #ffffff 0%, #eef2ff 55%, #f0fdfa 100%);
  overflow-y: auto;
  box-sizing: border-box;
}
.tcp-wrap::-webkit-scrollbar { width: 4px; }
.tcp-wrap::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); border-radius: 8px; }

/* Issue identity pill */
.tcp-issue-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e2e8f0;
  border-radius: 99px;
  padding: 5px 14px 5px 6px;
  max-width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  overflow: hidden;
  flex-shrink: 0;
}
.tcp-pill-ref {
  font-size: 11px;
  font-weight: 800;
  color: #6366f1;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 99px;
  padding: 2px 9px;
  white-space: nowrap;
  flex-shrink: 0;
}
.tcp-pill-name {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Clock card */
.tcp-clock-card {
  position: relative;
  width: 380px; height: 380px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow:
    0 0 0 1px rgba(226,232,240,0.9),
    0 4px 12px rgba(99,102,241,0.08),
    0 12px 40px rgba(99,102,241,0.10),
    0 32px 80px rgba(99,102,241,0.06),
    inset 0 1px 2px rgba(255,255,255,0.9);
  background: radial-gradient(ellipse at 35% 25%, #ffffff 0%, #f8faff 60%, #eff0ff 100%);
}
.tcp-svg {
  width: 100%; height: 100%;
  transform: rotate(-90deg);
  overflow: visible;
}
.tcp-arc {
  fill: none;
  stroke-width: 10;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.94s cubic-bezier(0.4, 0, 0.2, 1);
}
.tcp-dot { fill: #6366f1; }

/* digits overlay */
.tcp-digits-wrap {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 6px; pointer-events: none; user-select: none;
}
.tcp-time {
  font-size: 48px; font-weight: 800;
  font-variant-numeric: tabular-nums; letter-spacing: 3px; line-height: 1;
  background: linear-gradient(135deg, #3730a3 0%, #6366f1 50%, #0891b2 100%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.tcp-time--pulse { animation: tcp-pulse-time 0.65s ease; }
@keyframes tcp-pulse-time {
  0%   { transform: scale(1);    filter: brightness(1); }
  40%  { transform: scale(1.06); filter: brightness(1.15); }
  100% { transform: scale(1);    filter: brightness(1); }
}
.tcp-sub { display: flex; align-items: center; gap: 5px; }
.tcp-elapsed { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 1.8px; }
.tcp-sep     { font-size: 11px; color: #cbd5e1; }
.tcp-est     { font-size: 11px; font-weight: 600; color: #a5b4fc; }

/* H/M/S row */
.tcp-hms-row { width: 100%; max-width: 300px; gap: 8px; max-height:70px }
.tcp-hms-cell {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px;
  background: #fff; border: 1px solid #e8eaf6; border-radius: 14px;
  padding: 10px 6px; margin: 0 4px;
  /* max-height:40px; */
  box-shadow: 0 2px 8px rgba(99,102,241,0.06);
  transition: box-shadow 0.15s, transform 0.15s;
}
.tcp-hms-cell:hover { box-shadow: 0 4px 16px rgba(99,102,241,0.12); transform: translateY(-2px); }
.tcp-hms-val {
  font-size: 26px; font-weight: 800;
  font-variant-numeric: tabular-nums; line-height: 1;
  background: linear-gradient(135deg, #3730a3, #6366f1);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.tcp-hms-lbl { font-size: 9px; font-weight: 700; color: #94a3b8; letter-spacing: 1.2px; text-transform: uppercase; }

/* Progress card */
.tcp-progress-card {
  width: 100%; max-width: 300px;
  background: #fff; border: 1px solid #e8eaf6; border-radius: 14px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(99,102,241,0.05);
  display: flex; flex-direction: column; gap: 8px;
}
.tcp-progress-header { display: flex; justify-content: space-between; align-items: center; }
.tcp-progress-title {
  display: flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.6px;
}
.tcp-progress-pct { font-size: 13px; font-weight: 800; color: #6366f1; }
.tcp-pct--over { color: #ef4444; }
.tcp-progress-track {
  height: 8px; background: #f1f5f9; border-radius: 99px; overflow: hidden;
}
.tcp-progress-fill {
  height: 100%; border-radius: 99px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
  transition: width 1s linear; position: relative;
}
.tcp-progress-fill::after {
  content: ''; position: absolute; right: 0; top: 0; bottom: 0; width: 12px;
  background: rgba(255,255,255,0.5); border-radius: 99px;
  animation: tcp-shimmer 1.5s ease-in-out infinite;
}
@keyframes tcp-shimmer { 0%, 100% { opacity: 0; } 50% { opacity: 1; } }
.tcp-fill--over { background: linear-gradient(90deg, #f43f5e 0%, #f97316 100%); }
.tcp-progress-ticks {
  display: flex; justify-content: space-between;
  font-size: 9px; font-weight: 600; color: #cbd5e1; margin-top: -2px;
}

/* Stop button */
.tcp-stop-btn {
  position: relative; width: 100%; max-width: 210px; height: 48px;
  border: none; border-radius: 99px; cursor: pointer;
  background: transparent; padding: 0; overflow: hidden; transition: transform 0.12s;
}
.tcp-stop-btn:hover:not(:disabled) { transform: translateY(-2px); }
.tcp-stop-btn:active:not(:disabled){ transform: translateY(0); }
.tcp-stop-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.tcp-stop-ring {
  position: absolute; inset: 0; border-radius: 99px;
  border: 2px solid rgba(239,68,68,0.45);
  animation: tcp-ring 2.2s ease-out infinite; pointer-events: none;
}
@keyframes tcp-ring { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(1.14); opacity: 0; } }
.tcp-stop-content {
  position: relative; z-index: 1;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; height: 100%;
  background: rgba(239,68,68,0.07); border: 1.5px solid rgba(239,68,68,0.3); border-radius: 99px;
  transition: background 0.18s, border-color 0.18s, box-shadow 0.18s;
}
.tcp-stop-btn:hover:not(:disabled) .tcp-stop-content {
  background: rgba(239,68,68,0.13); border-color: rgba(239,68,68,0.55);
  box-shadow: 0 6px 24px rgba(239,68,68,0.18);
}
.tcp-stop-icon {
  width: 24px; height: 24px; border-radius: 50%;
  background: rgba(239,68,68,0.12);
  display: flex; align-items: center; justify-content: center;
}
.tcp-stop-sq { width: 8px; height: 8px; border-radius: 2px; background: #ef4444; }
.tcp-stop-label { font-size: 14px; font-weight: 700; color: #ef4444; letter-spacing: 0.3px; }

/* kbd hint */
.tcp-kbd-hint { font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 4px; }
kbd {
  display: inline-block; background: #f1f5f9;
  border: 1px solid #e2e8f0; border-bottom-width: 2px; border-radius: 4px;
  padding: 1px 6px; font-size: 10px; font-family: inherit; color: #64748b;
}
</style>
